import { MixerHorizontalIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  ChevronDownIcon,
  Flex,
  Popover,
  Separator,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateRangeInput } from 'components/common/form/date/range';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSwitchInput } from 'components/common/form/switch';
import usePitchPerformanceStore, {
  ENABLE_COMPARISON_PLOTS,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  differenceInDays,
  endOfDay,
  endOfToday,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import { t } from 'i18next';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useMemo, useState } from 'react';

export const PitchPerfControls = (props: { machine: IMachine }) => {
  const [maxDate] = useState(endOfToday());

  const {
    filters,
    loading,
    isAveraged,
    showTargets,
    showThresholds,
    setIsAveraged,
    setShowTargets,
    setShowThresholds,
    mergeFilters,
  } = usePitchPerformanceStore();

  const { currPeriod, prevPeriod } = filters;

  const [shotOptions] = useState<IOption[]>(
    [100, 250, 500, -1].map((n) => {
      const o: IOption = {
        label: n === -1 ? 'All shots' : `Last ${n} shots`,
        value: n.toString(),
      };

      return o;
    })
  );

  const ballTypeOptions = useMemo(
    () =>
      props.machine.ball_type_options.map((b) => {
        const o: IOption = {
          label: b,
          value: b,
        };

        return o;
      }),
    [props.machine.ball_type_options]
  );

  const trainingOptions: IOption[] = [
    {
      label: 'BP Shots',
      value: 'false',
    },
    {
      label: 'Training Shots',
      value: 'true',
    },
  ];

  return (
    <ErrorBoundary componentName="PitchPerfControls">
      <CommonFormGrid columns={5}>
        {/* row 1 */}
        <CommonDateRangeInput
          id="currPeriod"
          displayFormat="MMM d, yyyy"
          defaultStartValue={
            currPeriod.start ? parseISO(currPeriod.start) : undefined
          }
          defaultEndValue={
            currPeriod.end ? parseISO(currPeriod.end) : undefined
          }
          maxDate={maxDate}
          onChange={(values) => {
            if (!values || !values.start || !values.end) {
              NotifyHelper.warning({
                message_md: t('common.check-inputs-msg'),
              });
              return;
            }

            const currStart = startOfDay(values.start);
            const currEnd = endOfDay(values.end);

            // determine the same-length period before the current one
            // e.g. if the current period is 1/15 - 1/19, the previous period will be 1/10 - 1/14
            const rangeLength = differenceInDays(currEnd, currStart) + 1;
            const prevEnd = subDays(currStart, 1);
            const prevStart = subDays(prevEnd, rangeLength - 1);

            mergeFilters({
              currPeriod: {
                start: currStart.toISOString(),
                end: currEnd.toISOString(),
              },
              prevPeriod: {
                start: prevStart.toISOString(),
                end: prevEnd.toISOString(),
              },
            });
          }}
          disabled={loading}
          onChangeOnClose
        />

        <CommonSelectInput
          id="shots"
          value={filters.shots.toString()}
          options={shotOptions}
          onNumericChange={(v) => mergeFilters({ shots: v })}
          disabled={loading}
          skipSort
        />

        <CommonSelectInput
          id="ball_type"
          value={filters.ball_type}
          options={ballTypeOptions}
          onChange={(v) => mergeFilters({ ball_type: v as BallType })}
          disabled={loading}
        />

        <CommonSelectInput
          id="training"
          value={filters.training.toString()}
          options={trainingOptions}
          onChange={(v) => mergeFilters({ training: v === 'true' })}
          disabled={loading}
        />

        <Box>
          <Popover.Root>
            <Popover.Trigger>
              <Button
                className="btn-block"
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                color={RADIX.COLOR.NEUTRAL}
              >
                <MixerHorizontalIcon />
                <Text truncate>Display Options</Text>
                <ChevronDownIcon />
              </Button>
            </Popover.Trigger>
            <Popover.Content>
              <Card>
                <Flex direction="column" gap="2">
                  <CommonSwitchInput
                    id="is-averaged"
                    label="Pitch Averages"
                    size="1"
                    checked={isAveraged}
                    onCheckedChange={(v) => setIsAveraged(v)}
                    justify
                    reverse
                  />

                  <Separator size="4" />

                  <CommonSwitchInput
                    id="show-thresholds"
                    label="Thresholds"
                    size="1"
                    checked={showThresholds}
                    onCheckedChange={(v) => setShowThresholds(v)}
                    justify
                    reverse
                  />

                  <Separator size="4" />

                  <CommonSwitchInput
                    id="show-targets"
                    label="Targets"
                    size="1"
                    checked={showTargets}
                    onCheckedChange={(v) => setShowTargets(v)}
                    justify
                    reverse
                  />
                </Flex>
              </Card>
            </Popover.Content>
          </Popover.Root>
        </Box>

        {/* row 2 */}
        {ENABLE_COMPARISON_PLOTS && (
          <CommonDateRangeInput
            id="prevPeriod"
            displayFormat="MMM d, yyyy"
            defaultStartValue={
              prevPeriod.start ? parseISO(prevPeriod.start) : undefined
            }
            defaultEndValue={
              prevPeriod.end ? parseISO(prevPeriod.end) : undefined
            }
            maxDate={maxDate}
            onChange={(values) => {
              if (!values) {
                NotifyHelper.warning({
                  message_md: t('common.check-inputs-msg'),
                });
                return;
              }

              mergeFilters({
                prevPeriod: {
                  start: values.start
                    ? startOfDay(values.start).toISOString()
                    : undefined,
                  end: values.end
                    ? endOfDay(values.end).toISOString()
                    : undefined,
                },
              });
            }}
            disabled={!filters.comparing}
          />
        )}

        {ENABLE_COMPARISON_PLOTS && (
          <CommonSwitchInput
            id="comparing"
            label="Compare"
            checked={filters.comparing}
            onCheckedChange={(v) => mergeFilters({ comparing: v })}
          />
        )}
      </CommonFormGrid>
    </ErrorBoundary>
  );
};
